import getMilestone, { IMilestoneCategory, IMilestoneData, UIMilestone } from '../api/getMilestone';
import groupBy from 'lodash/groupBy';
import { IMilestoneHistory } from '../components/common/types/milestones';
import isEmpty from 'lodash/isEmpty';
import {
    MISC_KEYS,
    bulkPutMilestones,
    clearAllMilestones,
    getItemFromMisc,
    getMilestones,
    updateItemInMisc,
} from '../store/idbHandler';
import { MilestoneUpdatedAt } from '../constants';
import { captureError } from '../pages/captureError';
import { milestoneCategories } from '../components/common/constants/milestones';
import { getMyDbId } from '../api/mactiveid';

export const convertMilestoneObjectForUI = (milestones: IMilestoneData[]): UIMilestone => {
    const groupedByAgeGroup = groupBy(milestones, 'ageGroup');
    const ageGroups = Object.keys(groupedByAgeGroup);
    let result: any = {};

    for (let i = 0; i < ageGroups.length; i++) {
        const ageG = ageGroups[i];
        const ageGArray = groupedByAgeGroup[ageG];
        const groupedByCategory = groupBy(ageGArray, 'categoryId');
        const categoriesFromData = Object.keys(groupedByCategory);
        const categories = milestoneCategories.filter((m) => categoriesFromData.includes(m));
        result = { ...result, [ageG]: { meta: { hrsFromBirth: ageGArray[0]?.maxHrsFromBirth } } };

        for (let j = 0; j < categories.length; j++) {
            const category = categories[j];
            const categoryArray = groupedByCategory[category];

            for (let k = 0; k < categoryArray.length; k++) {
                const {
                    ageGroup,
                    categoryId,
                    id,
                    name,
                    maxHrsFromBirth,
                    created_at,
                    updated_at,
                    category: categoryName,
                } = categoryArray[k];
                const currRes = result[ageG] ?? {};
                const currResData = currRes.data ?? {};

                result[ageG] = {
                    ...currRes,
                    data: {
                        ...currResData,
                        [category]: {
                            ...(currResData?.[category] ?? {}),
                            [id]: {
                                id,
                                name,
                                categoryId,
                                ageGroup,
                                maxHrsFromBirth,
                                created_at,
                                updated_at,
                                category: categoryName,
                            },
                        },
                    },
                };
            }
        }
    }

    return result;
};

export const getMilestoneSummary = (
    milestonesTillCurrAge: IMilestoneData[],
    markedMilestonesAll: IMilestoneHistory[],
) => {
    const markedMilestonesTillAge = markedMilestonesAll.filter((m) =>
        milestonesTillCurrAge.map((val) => val.id).includes(m.id),
    );

    const clubbedMilestones = markedMilestonesTillAge.reduce((prev, curr) => {
        if (curr?.status) {
            const status =
                curr.status === 'achieved' ||
                curr.status === 'achieved_delay' ||
                curr.status === 'achieved_early' ||
                curr.status === 'achieved_unknown'
                    ? 'achieved'
                    : curr.status;

            prev[status] = [...(prev[status] ?? []), curr];
        }

        return prev;
    }, {} as any);

    const groupedMilestonesbyCat = groupBy(milestonesTillCurrAge, 'categoryId');
    const allCategoriesTillDate = Object.keys(groupedMilestonesbyCat);
    const statuses = Object.keys(clubbedMilestones);

    const result: any = {};

    for (let i = 0; i < allCategoriesTillDate.length; i++) {
        const currCat = allCategoriesTillDate[i];
        const currCatMilestones = groupedMilestonesbyCat[currCat];

        result[currCat] = {};

        for (let j = 0; j < statuses.length; j++) {
            const currStatus = statuses[j];

            if (currStatus) {
                const currStatusMarkedMilestones = clubbedMilestones[currStatus].filter(
                    (m: any) => m.categoryId === currCat,
                );

                if (currStatus === 'achieved') {
                    result[currCat][currStatus] =
                        currStatusMarkedMilestones.length === currCatMilestones.length;
                }
            }
        }
    }
    const groupedAllMilestones = groupBy(markedMilestonesAll, 'categoryId');

    Object.keys(groupedAllMilestones).forEach((key) => {
        if (groupedAllMilestones?.[key]?.length) {
            const hasDelayed = groupedAllMilestones?.[key].find((m) => m.status === 'delayed');

            if (hasDelayed) {
                result[key]['delayed'] = true;
            }

            const hasUnderObservation = groupedAllMilestones?.[key].find(
                (m) => m.status === 'under_observation',
            );

            if (hasUnderObservation) {
                result[key]['under_observation'] = true;
            }
        }
    });

    return isEmpty(result)
        ? { statuses: markedMilestonesAll }
        : { result, statuses: markedMilestonesAll };
};

export const generateCategoryMap = (
    ageGroupTillCurrage: string[],
    milestoneData: UIMilestone,
    markedMilestones: IMilestoneHistory[],
): Record<string, boolean> => {
    const result: { id: string; categoryId: string }[] = [];

    for (let i = 0; i < ageGroupTillCurrage.length; i++) {
        const currAge = ageGroupTillCurrage[i];
        const currData = milestoneData[currAge].data;
        const values = Object.values(currData);

        for (let j = 0; j < values.length; j++) {
            const m = values[j];
            const milestones = Object.values(m).map((m) => ({
                id: m.id,
                categoryId: m.categoryId,
            }));

            result.push(...milestones);
        }
    }

    const groupedResult = groupBy(result, 'categoryId');
    const resultIds = result.map((r) => r.id);
    const markedMilestonesTillAge = markedMilestones.filter((m) => resultIds.includes(m.id));

    return Object.keys(groupedResult).reduce((prev, curr) => {
        const milestones = groupedResult[curr];
        const markedMilestonesTillAgeCurrCat = markedMilestonesTillAge.filter(
            (m) =>
                m.categoryId === curr &&
                (m.status === 'achieved' ||
                    m.status === 'achieved_delay' ||
                    m.status === 'achieved_early'),
        );
        prev[curr] = markedMilestonesTillAgeCurrCat.length === milestones.length;
        return prev;
    }, {} as Record<string, boolean>);
};

export const fetchMilestones = async (): Promise<void> => {
    try {
        const lastUpdated = await getItemFromMisc<MilestoneUpdatedAt>(
            getMyDbId() || '',
            MISC_KEYS.milestonesLastUpdatedAt,
        );

        const isoString = lastUpdated.isoString;
        const milestoneDataVersion = lastUpdated.version;

        const value = await getMilestone(isoString ?? undefined, milestoneDataVersion ?? undefined);

        if (value.error || value.success === false) {
            throw new Error(value.error);
        }

        if (value.milestones?.length) {
            if (value.version !== milestoneDataVersion) {
                await clearAllMilestones(getMyDbId() || '');
            }

            bulkPutMilestones(getMyDbId() || '', value.milestones);
            updateItemInMisc(getMyDbId() || '', MISC_KEYS.milestonesLastUpdatedAt, {
                isoString: new Date().toISOString(),
                version: value.version,
            });
        }
    } catch (error) {
        captureError(error as Error);
    }
};

let uiMilestones: UIMilestone | undefined;

export const milestonesUICache = async (): Promise<UIMilestone> => {
    if (uiMilestones) {
        return uiMilestones;
    }

    const res = await getMilestones(getMyDbId() || '');
    const finalResponse =
        res?.filter((m) => !m.archived).sort((a, b) => a.maxHrsFromBirth - b.maxHrsFromBirth) || [];
    uiMilestones = convertMilestoneObjectForUI(finalResponse);
    return uiMilestones;
};

export const flushUIMilestonesCache = async (): Promise<void> => {
    uiMilestones = undefined;
};
